import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

/** User type filter for ListUsers (lowercase as in API) */
export type ListUsersUserType = 'doctor' | 'nurse' | 'frontdesk' | 'biller';

/** Input for ListUsers GraphQL query */
export interface ListUsersInput {
  page: number;
  limit: number;
  /** Search: username, first name, last name, or description */
  query?: string;
  /**
   * Filter by a single user type (legacy).
   * Kept for compatibility but backend now prefers userTypes array.
   */
  userType?: ListUsersUserType;
  /**
   * Filter by multiple user types.
   * Example: ['nurse'] or ['doctor', 'biller'].
   */
  userTypes?: ListUsersUserType[];
  /** Filter by active status; omit for "all" */
  isActive?: boolean;
  /** Filter by one or more role IDs */
  roleIds?: number[];
  /** Filter by one or more practice IDs */
  practiceIds?: number[];
}

/** Single user from listUsers GraphQL data */
export interface ListUsersDataItem {
  userId?: number;
  userName?: string;
  userFirstName?: string;
  userLastName?: string;
  userTypeFrontDesk?: boolean;
  userTypeDoctor?: boolean;
  userTypeNurse?: boolean;
  userTypeBiller?: boolean;
  userTypePatient?: boolean;
  userDescription?: string;
  userIsActive?: boolean;
  isActive?: boolean;
  userWorkFromTime?: string;
  userWorkToTime?: string;
  enteredBy?: string | number;
  enteredByName?: string;
  enteredDate?: string;
  /** Effective EMR access (admin, role menu, or direct grant). */
  canAccessEMR?: boolean;
}

/** Result of listUsers GraphQL query (API may return total, page, limit, totalPages as strings) */
export interface ListUsersGqlResult {
  message?: string;
  total?: number | string;
  page?: number | string;
  limit?: number | string;
  totalPages?: number | string;
  data?: ListUsersDataItem[];
}

/** Input for listUserRoles GraphQL query */
export interface ListUserRolesInput {
  page: number;
  limit: number;
  role?: string;
  description?: string;
  isActive?: boolean;
}

/** Single role from listUserRoles GraphQL data */
export interface ListUserRolesDataItem {
  roleId?: number;
  roleName?: string;
  roleDescription?: string;
  isActive?: boolean;
  enteredBy?: string | number;
  enteredByName?: string;
  enteredDate?: string;
}

/** Result of listUserRoles GraphQL query */
export interface ListUserRolesGqlResult {
  message?: string;
  total?: number | string;
  page?: number | string;
  limit?: number | string;
  totalPages?: number | string;
  data?: ListUserRolesDataItem[];
}

/** Row shape returned by listUserRolesGraphQL for User Roles screen */
export interface UserRoleListRow {
  roleId?: number;
  role: string;
  description: string;
  isActive: boolean;
  enteredBy: string;
  enteredDate: string;
}

/** Row for User Master table and Add/Edit User form (matches listUsers + local filter state) */
export interface UserMasterRow {
  userId?: number;
  userName: string;
  firstName: string;
  lastName: string;
  description: string;
  isActive: boolean;
  enteredBy: string;
  enteredDate: string;
  /** For Add/Edit User modal pre-fill */
  userTypeFrontDesk?: boolean;
  userTypeNurse?: boolean;
  userTypeDoctor?: boolean;
  userTypeBiller?: boolean;
  userWorkFromTime?: string;
  userWorkToTime?: string;
  /** Effective EMR access from ListUsers GraphQL. */
  canAccessEMR?: boolean;
}

export interface UserTypesDto {
  doctor?: boolean;
  nurse?: boolean;
  frontDesk?: boolean;
  biller?: boolean;
}

export interface UserListItem {
  userId: number;
  username: string;
  firstName: string;
  lastName: string;
  userTypes: UserTypesDto;
  isActive?: boolean;
  canAccessEMR?: boolean;
}

export interface ListUsersParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListUsersResponse {
  users?: UserListItem[];
  data?: UserListItem[] | { users?: UserListItem[] };
  totalCount?: number;
  totalPages?: number;
}

/** Normalize API user (camelCase or snake_case) to UserListItem */
function mapUserFromApi(raw: any): UserListItem {
  const ut = raw?.userTypes ?? raw?.user_types ?? {};
  return {
    userId: Number(raw?.userId ?? raw?.user_id ?? 0),
    username: String(raw?.username ?? raw?.user_name ?? ''),
    firstName: String(raw?.firstName ?? raw?.first_name ?? ''),
    lastName: String(raw?.lastName ?? raw?.last_name ?? ''),
    userTypes: {
      doctor: !!(ut?.doctor ?? ut?.user_type_doctor),
      nurse: !!(ut?.nurse ?? ut?.user_type_nurse),
      frontDesk: !!(ut?.frontDesk ?? ut?.front_desk ?? ut?.user_type_front_desk),
      biller: !!(ut?.biller ?? ut?.user_type_biller)
    },
    isActive: raw?.isActive !== undefined ? !!raw.isActive : raw?.is_active !== undefined ? !!raw.is_active : true
  };
}

/** GraphQL fields requested on each user in listUsers.data */
const LIST_USERS_DATA_FIELDS = `
            userId
            userName
            userFirstName
            userLastName
            userTypeFrontDesk
            userTypeDoctor
            userTypeNurse
            userTypeBiller
            userTypePatient
            userDescription
            userIsActive
            isActive
            userWorkFromTime
            userWorkToTime
            enteredBy
            enteredByName
            enteredDate
            canAccessEMR`;

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {

  constructor(private api: ApiService) {}

  /**
   * GET /api/v1/profile/roles – list roles for dropdown (e.g. Security Users filter).
   * Response: { data: [ { roleId, roleName, roleDescription, isActive, ... } ] }.
   * Returns options with label = roleName and value = roleId so selection gives ID for roleIds filter.
   */
  getRoles(): Observable<{ label: string; value: string }[]> {
    return this.api.get<any>('profile/roles').pipe(
      map((res: any) => {
        const arr = Array.isArray(res) ? res : (res?.data ?? res?.roles ?? []);
        return arr
          .map((item: any) => {
            if (item == null) return null;
            if (typeof item === 'string') {
              return { label: item, value: item };
            }
            const label = item?.roleName ?? item?.name ?? item?.role ?? item?.label ?? '';
            const id = item?.roleId ?? item?.value;
            const value = id != null ? String(id) : label;
            if (!label && !value) return null;
            return { label: String(label || value), value: String(value) };
          })
          .filter((o: { label: string; value: string } | null) => !!o && o.label !== '');
      }),
      catchError(() => throwError(() => new Error('Failed to load roles')))
    );
  }

  /**
   * GET list of users for admin (e.g. security/users or users).
   * Response may be array or { data: [] } or { users: [] }.
   */
  getUsers(params?: ListUsersParams): Observable<{ users: UserListItem[]; totalCount?: number }> {
    const queryParams: Record<string, string | number> = {};
    if (params?.['search'] != null && String(params['search']).trim() !== '') {
      queryParams['search'] = String(params['search']).trim();
    }
    if (params?.['page'] != null) queryParams['page'] = params['page'];
    if (params?.['limit'] != null) queryParams['limit'] = params['limit'];

    return this.api.get<ListUsersResponse | UserListItem[]>('security/users', {
      params: queryParams as any
    }).pipe(
      map((res: any) => {
        let list: UserListItem[] = [];
        if (Array.isArray(res)) {
          list = res.map(mapUserFromApi);
        } else if (res?.users && Array.isArray(res.users)) {
          list = res.users.map(mapUserFromApi);
        } else if (res?.data) {
          const d = res.data;
          list = Array.isArray(d) ? d.map(mapUserFromApi) : (d?.users ?? []).map(mapUserFromApi);
        }
        return { users: list, totalCount: res?.totalCount ?? res?.total_count ?? list.length };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * GraphQL listUsers – fetches users for User Master screen.
   * Maps response to UserMasterRow[] (userName, description, isActive, enteredBy, enteredDate).
   */
  listUsersGraphQL(input: ListUsersInput): Observable<{ rows: UserMasterRow[]; total: number; page: number; limit: number; totalPages?: number }> {
    const query = `
      query ListUsers($input: ListUsersInput!) {
        listUsers(input: $input) {
          message
          total
          page
          limit
          totalPages
          data {${LIST_USERS_DATA_FIELDS}
          }
        }
      }
    `;
    // Build GraphQL input: userTypes, roleIds, practiceIds
    const gqlInput: any = {
      page: input.page,
      limit: input.limit,
      query: input.query,
      isActive: input.isActive
    };
    if (input.userTypes && input.userTypes.length > 0) {
      gqlInput.userTypes = input.userTypes;
    } else if (input.userType) {
      gqlInput.userTypes = [input.userType];
    }
    if (input.roleIds && input.roleIds.length > 0) {
      gqlInput.roleIds = input.roleIds;
    }
    if (input.practiceIds && input.practiceIds.length > 0) {
      gqlInput.practiceIds = input.practiceIds;
    }

    return this.api.graphql<{ listUsers: ListUsersGqlResult }>(query, { input: gqlInput }).pipe(
      map((res) => this.mapListUsersGqlResponse(res, input)),
      catchError((err) => throwError(() => err))
    );
  }

  /** Shared ListUsers GraphQL response mapper (handles nested data envelopes). */
  private mapListUsersGqlResponse(
    res: any,
    input: ListUsersInput
  ): { rows: UserMasterRow[]; total: number; page: number; limit: number; totalPages: number } {
        const list = res?.listUsers ?? res?.data?.listUsers ?? res;
        const rawData = list?.data;
        const data = Array.isArray(rawData) ? rawData : [];
        const rows: UserMasterRow[] = data.map((item) => ({
          userId: item?.userId != null ? Number(item.userId) : item?.user_id != null ? Number(item.user_id) : undefined,
          userName: String(item?.userName ?? item?.user_name ?? ''),
          firstName: String(item?.userFirstName ?? item?.user_first_name ?? ''),
          lastName: String(item?.userLastName ?? item?.user_last_name ?? ''),
          description: String(item?.userDescription ?? ''),
          isActive: item?.userIsActive ?? item?.isActive ?? true,
          enteredBy: String(item?.enteredByName ?? item?.enteredBy ?? ''),
          enteredDate: this.formatEnteredDate(item?.enteredDate),
          userTypeFrontDesk: !!item?.userTypeFrontDesk,
          userTypeNurse: !!item?.userTypeNurse,
          userTypeDoctor: !!item?.userTypeDoctor,
          userTypeBiller: !!item?.userTypeBiller,
          userWorkFromTime: item?.userWorkFromTime ?? undefined,
          userWorkToTime: item?.userWorkToTime ?? undefined,
          canAccessEMR: !!(item?.canAccessEMR ?? item?.can_access_emr)
        }));
        const total = this.toNumber(list?.total, rows.length);
        const page = this.toNumber(list?.page, input.page);
        const limit = this.toNumber(list?.limit, input.limit);
        const totalPages = this.toNumber(list?.totalPages, total > 0 && limit > 0 ? Math.ceil(total / limit) : 1);
        return {
          rows,
          total,
          page,
          limit,
          totalPages
        };
  }

  /**
   * ListUsers GraphQL for User Role screen: same query with query, userType, isActive filters.
   * Returns users as UserListItem[] with userTypes for role checkboxes.
   */
  listUsersForRole(input: ListUsersInput): Observable<{ users: UserListItem[]; total: number; page: number; limit: number; totalPages: number }> {
    const query = `
      query ListUsers($input: ListUsersInput!) {
        listUsers(input: $input) {
          message
          total
          page
          limit
          totalPages
          data {${LIST_USERS_DATA_FIELDS}
          }
        }
      }
    `;
    const gqlInput: ListUsersInput = { ...input };
    if (input.userTypes && input.userTypes.length > 0) {
      gqlInput.userTypes = input.userTypes;
    } else if (input.userType) {
      gqlInput.userTypes = [input.userType];
      delete (gqlInput as { userType?: ListUsersUserType }).userType;
    }

    return this.api.graphql<{ listUsers: ListUsersGqlResult }>(query, { input: gqlInput }).pipe(
      map((res: any) => {
        const mapped = this.mapListUsersGqlResponse(res, input);
        const users: UserListItem[] = mapped.rows.map((row) => ({
          userId: row.userId ?? 0,
          username: row.userName,
          firstName: row.firstName,
          lastName: row.lastName,
          userTypes: {
            doctor: !!row.userTypeDoctor,
            nurse: !!row.userTypeNurse,
            frontDesk: !!row.userTypeFrontDesk,
            biller: !!row.userTypeBiller
          },
          isActive: row.isActive,
          canAccessEMR: row.canAccessEMR
        }));
        return {
          users,
          total: mapped.total,
          page: mapped.page,
          limit: mapped.limit,
          totalPages: mapped.totalPages
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * GraphQL listUserRoles – fetches roles for User Roles screen (latest created first).
   */
  listUserRolesGraphQL(input: ListUserRolesInput): Observable<{ rows: UserRoleListRow[]; total: number; page: number; limit: number; totalPages: number }> {
    const query = `
      query ListUserRoles($input: ListUserRolesInput!) {
        listUserRoles(input: $input) {
          message
          total
          page
          limit
          totalPages
          data {
            roleId
            roleName
            roleDescription
            isActive
            enteredBy
            enteredByName
            enteredDate
          }
        }
      }
    `;
    return this.api.graphql<{ listUserRoles: ListUserRolesGqlResult }>(query, { input }).pipe(
      map((res) => {
        const list = res?.listUserRoles;
        const data = list?.data ?? [];
        const rows: UserRoleListRow[] = data.map((item: ListUserRolesDataItem) => ({
          roleId: item?.roleId != null ? Number(item.roleId) : undefined,
          role: String(item?.roleName ?? ''),
          description: String(item?.roleDescription ?? ''),
          isActive: !!item?.isActive,
          enteredBy: String(item?.enteredByName ?? item?.enteredBy ?? ''),
          enteredDate: this.formatEnteredDate(item?.enteredDate)
        }));
        const total = this.toNumber(list?.total, rows.length);
        const page = this.toNumber(list?.page, input.page);
        const limit = this.toNumber(list?.limit, input.limit);
        const totalPages = this.toNumber(list?.totalPages, total > 0 && limit > 0 ? Math.ceil(total / limit) : 1);
        return { rows, total, page, limit, totalPages };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /** Parse API value that may be string or number */
  private toNumber(value: string | number | undefined, fallback: number): number {
    if (value == null) return fallback;
    if (typeof value === 'number' && !isNaN(value)) return value;
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    return isNaN(n) ? fallback : n;
  }

  private formatEnteredDate(value: string | undefined): string {
    if (value == null || value === '') return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  /**
   * GET profile/roles/{roleId} – fetch RoleDetails including practiceIds and providerIds.
   */
  getRoleDetails(roleId: number): Observable<{
    roleId: number;
    roleName: string;
    roleDescription: string;
    isActive: boolean;
    practiceIds: number[];
    providerIds: number[];
    enteredBy?: number;
    enteredByName?: string;
    enteredDate?: string;
  }> {
    return this.api.get<any>(`profile/roles/${roleId}`).pipe(
      map((res: any) => {
        const d = res?.data ?? res;
        const rawPracticeIds: any[] =
          Array.isArray(d?.practiceIds) && d.practiceIds.length
            ? d.practiceIds
            : Array.isArray(d?.practices)
              ? d.practices
              : [];
        const rawProviderIds: any[] =
          Array.isArray(d?.providerIds) && d.providerIds.length
            ? d.providerIds
            : Array.isArray(d?.providers)
              ? d.providers
              : [];
        return {
          roleId: Number(d?.roleId ?? d?.id ?? roleId),
          roleName: String(d?.roleName ?? d?.name ?? ''),
          roleDescription: String(d?.roleDescription ?? d?.description ?? ''),
          isActive: d?.isActive !== undefined ? !!d.isActive : true,
          practiceIds: rawPracticeIds.map((x: any) => Number(x)),
          providerIds: rawProviderIds.map((x: any) => Number(x)),
          enteredBy: d?.enteredBy != null ? Number(d.enteredBy) : undefined,
          enteredByName: d?.enteredByName ?? '',
          enteredDate: d?.enteredDate
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * POST /api/v1/profile/roles – create a new role with practice/provider links.
   */
  addRole(payload: {
    roleName: string;
    roleDescription?: string;
    isActive: boolean;
    practiceIds: number[];
    providerIds: number[];
  }): Observable<{
    roleId: number;
    roleName: string;
    roleDescription: string;
    isActive: boolean;
    practiceIds: number[];
    providerIds: number[];
    enteredBy?: number;
    enteredByName?: string;
    enteredDate?: string;
  }> {
    return this.api.post<any>('profile/roles', payload).pipe(
      map((res: any) => {
        const d = res?.data ?? res;
        return {
          roleId: Number(d?.roleId ?? d?.id ?? 0),
          roleName: String(d?.roleName ?? payload.roleName),
          roleDescription: String(d?.roleDescription ?? payload.roleDescription ?? ''),
          isActive: d?.isActive !== undefined ? !!d.isActive : payload.isActive,
          practiceIds: Array.isArray(d?.practiceIds) ? d.practiceIds.map((x: any) => Number(x)) : payload.practiceIds ?? [],
          providerIds: Array.isArray(d?.providerIds) ? d.providerIds.map((x: any) => Number(x)) : payload.providerIds ?? [],
          enteredBy: d?.enteredBy != null ? Number(d.enteredBy) : undefined,
          enteredByName: d?.enteredByName ?? '',
          enteredDate: d?.enteredDate
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * PATCH /api/v1/profile/roles/{roleId} – update an existing role.
   */
  updateRole(roleId: number, payload: {
    roleName?: string;
    roleDescription?: string;
    isActive?: boolean;
    practiceIds?: number[];
    providerIds?: number[];
  }): Observable<{
    roleId: number;
    roleName: string;
    roleDescription: string;
    isActive: boolean;
    practiceIds: number[];
    providerIds: number[];
    enteredBy?: number;
    enteredByName?: string;
    enteredDate?: string;
  }> {
    return this.api.patch<any>(`profile/roles/${roleId}`, payload).pipe(
      map((res: any) => {
        const d = res?.data ?? res;
        return {
          roleId: Number(d?.roleId ?? d?.id ?? roleId),
          roleName: String(d?.roleName ?? payload.roleName ?? ''),
          roleDescription: String(d?.roleDescription ?? payload.roleDescription ?? ''),
          isActive: d?.isActive !== undefined ? !!d.isActive : (payload.isActive ?? true),
          practiceIds: Array.isArray(d?.practiceIds) ? d.practiceIds.map((x: any) => Number(x)) : (payload.practiceIds ?? []),
          providerIds: Array.isArray(d?.providerIds) ? d.providerIds.map((x: any) => Number(x)) : (payload.providerIds ?? []),
          enteredBy: d?.enteredBy != null ? Number(d.enteredBy) : undefined,
          enteredByName: d?.enteredByName ?? '',
          enteredDate: d?.enteredDate
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * DELETE /api/v1/profile/roles/{roleId} – delete a role and its practice/provider links.
   */
  deleteRole(roleId: number): Observable<void> {
    return this.api.delete<any>(`profile/roles/${roleId}`).pipe(
      map(() => void 0),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * PATCH/PUT update a user's roles (and optionally isActive).
   * Path: security/users/{userId}
   */
  updateUser(userId: number, payload: { userTypes?: UserTypesDto; isActive?: boolean }): Observable<unknown> {
    return this.api.patch<unknown>(`security/users/${userId}`, payload).pipe(
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Get user profile by ID – GET /api/v1/profile/users/{userId}
   * Used to prefill Edit User form (roles, work times, emergency, etc.).
   */
  getProfileUser(userId: number): Observable<ProfileUserResponse> {
    return this.api.get<ProfileUserResponse>(`profile/users/${userId}`).pipe(
      map((res: any) => {
        const data = res?.data ?? res;
        return Array.isArray(data) ? data[0] : data;
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Update user – PATCH /api/v1/profile/users/{userId}
   * Same shape as create; all fields optional (only send what you want to change).
   */
  updateProfileUser(userId: number, payload: UpdateUserPayload): Observable<{ message?: string; data?: unknown }> {
    return this.api.patch<{ message?: string; data?: unknown }>(`profile/users/${userId}`, payload).pipe(
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Create user – POST /api/v1/profile/users
   * Required: userName, userFirstName, userLastName, password, confirmPassword.
   * Optional: userDescription, userTypes, work times, roleIds, etc.
   */
  createUser(payload: CreateUserPayload): Observable<{ message?: string; data?: unknown }> {
    return this.api.post<{ message?: string; data?: unknown }>('profile/users', payload).pipe(
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Delete user – DELETE /api/v1/profile/users/{userId}
   * No body. Success: { message, data: { userId } }.
   */
  deleteUser(userId: number): Observable<{ message?: string; data?: { userId?: number } }> {
    return this.api.delete<{ message?: string; data?: { userId?: number } }>(`profile/users/${userId}`).pipe(
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * GET /api/v1/prescription-access/users – EMR access flags for all active users (admin).
   */
  getPrescriptionAccessUsers(): Observable<{ items: PrescriptionAccessUserItem[]; totalCount: number }> {
    return this.api.get<any>('prescription-access/users').pipe(
      map((res: any) => {
        const d = res?.data ?? res;
        const raw = Array.isArray(d?.items) ? d.items : [];
        const items: PrescriptionAccessUserItem[] = raw.map((item: any) => ({
          userId: Number(item?.userId ?? 0),
          userName: String(item?.userName ?? ''),
          userFirstName: String(item?.userFirstName ?? ''),
          userLastName: String(item?.userLastName ?? ''),
          hasEMRAccess: !!(item?.hasEMRAccess ?? item?.has_emr_access)
        }));
        return {
          items,
          totalCount: this.toNumber(d?.totalCount, items.length)
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * PUT /api/v1/prescription-access/users/{userId} – grant or revoke direct EMR access.
   */
  updatePrescriptionAccessUser(
    userId: number,
    hasEMRAccess: boolean,
    isEmergency = false
  ): Observable<PrescriptionAccessUserItem> {
    return this.api.put<any>(`prescription-access/users/${userId}`, { hasEMRAccess, isEmergency }).pipe(
      map((res: any) => {
        const d = res?.data ?? res;
        return {
          userId: Number(d?.userId ?? userId),
          userName: String(d?.userName ?? ''),
          userFirstName: String(d?.userFirstName ?? ''),
          userLastName: String(d?.userLastName ?? ''),
          hasEMRAccess: !!(d?.hasEMRAccess ?? hasEMRAccess)
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }
}

/** Row from GET /prescription-access/users */
export interface PrescriptionAccessUserItem {
  userId: number;
  userName: string;
  userFirstName: string;
  userLastName: string;
  hasEMRAccess: boolean;
}

/** Response from GET /api/v1/profile/users/{userId} (and create response data) */
export interface ProfileUserResponse {
  userId?: number;
  userName?: string;
  userFirstName?: string;
  userLastName?: string;
  password?: string;
  userDescription?: string;
  userTypeFrontDesk?: boolean;
  userTypeDoctor?: boolean;
  userTypeNurse?: boolean;
  userTypeBiller?: boolean;
  userTypePatient?: boolean;
  userWorkFromTime?: string;
  userWorkToTime?: string;
  userIdleTime?: number;
  userIsActive?: boolean;
  userEmergencyPassword?: string;
  userStartEmergencyRoleDate?: string;
  userEndEmergencyRoleDate?: string;
  userTypeEmergencyFrontDesk?: boolean;
  userTypeEmergencyDoctor?: boolean;
  userTypeEmergencyNurse?: boolean;
  accountNo?: number;
  roles?: { roleId?: number; roleName?: string }[];
}

/** Request body for PATCH /api/v1/profile/users/{userId} (Edit User) – all optional */
export interface UpdateUserPayload {
  userFirstName?: string;
  userLastName?: string;
  userDescription?: string;
  password?: string;
  confirmPassword?: string;
  userTypeFrontDesk?: boolean;
  userTypeDoctor?: boolean;
  userTypeNurse?: boolean;
  userTypeBiller?: boolean;
  userTypePatient?: boolean;
  userWorkFromTime?: string;
  userWorkToTime?: string;
  userIdleTime?: number;
  userIsActive?: boolean;
  userEmergencyPassword?: string;
  userStartEmergencyRoleDate?: string;
  userEndEmergencyRoleDate?: string;
  userTypeEmergencyFrontDesk?: boolean;
  userTypeEmergencyDoctor?: boolean;
  userTypeEmergencyNurse?: boolean;
  roleIds?: number[];
}

/** Request body for POST /api/v1/profile/users (Add User) */
export interface CreateUserPayload {
  userName: string;
  userFirstName: string;
  userLastName: string;
  password: string;
  confirmPassword: string;
  userDescription?: string;
  userTypeFrontDesk?: boolean;
  userTypeDoctor?: boolean;
  userTypeNurse?: boolean;
  userTypeBiller?: boolean;
  userTypePatient?: boolean;
  userWorkFromTime?: string;
  userWorkToTime?: string;
  userIdleTime?: number;
  userIsActive?: boolean;
  userEmergencyPassword?: string;
  userStartEmergencyRoleDate?: string;
  userEndEmergencyRoleDate?: string;
  userTypeEmergencyFrontDesk?: boolean;
  userTypeEmergencyDoctor?: boolean;
  userTypeEmergencyNurse?: boolean;
  roleIds?: number[];
  accountNo?: number;
}
